/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { CopyButton } from '@/primitives/CopyButton';
import { tokens } from '@/theme';

export type MimicSyntaxHighlighterProps = {
  source: string | undefined;
  language?: 'go' | 'json';
};

export function MimicSyntaxHighlighter({ source, language = 'go' }: MimicSyntaxHighlighterProps) {
  const { t } = useMimicTranslation('mimicSyntaxHighlighter');

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: tokens.color.surface.surfaceLow,
          border: `1px solid ${tokens.color.border.borderDefault}`,
          padding: '8px',
        }}
      >
        <CopyButton textToCopy={source || ''} size="sm" failedText={t('failedCopy')} />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', fontFamily: 'DM Mono' }}>
        <SyntaxHighlighter
          language={language}
          style={docco}
          showLineNumbers
          lineNumberStyle={{
            paddingLeft: '5px',
            paddingRight: '5px',
            marginRight: '15px',
            height: '26px',
            color: '#191919',
            fontSize: '1rem',
            backgroundColor: 'rgb(25, 25, 25, 0.04)',
            fontFamily: 'DM Mono',
            width: '50px',
          }}
          customStyle={{
            backgroundColor: 'white',
            border: '1px solid rgb(25, 25, 25, 0.1)',
            fontSize: '14px',
            padding: '0',
            margin: '0',
          }}
        >
          {ensureThirtyLines(source)}
        </SyntaxHighlighter>
      </div>
    </>
  );
}

function ensureThirtyLines(source = '') {
  const lines = source.split('\n');
  const lineCount = lines.length;

  if (lineCount < 15) {
    const additionalLines = 15 - lineCount;
    for (let i = 0; i < additionalLines; i += 1) {
      lines.push('');
    }
  }

  return lines.join('\n');
}
